import { Post } from '../types/post';
import { getCurrentIdToken } from '../auth/AuthContext';

/**
 * APIレスポンスの型定義
 */
export interface GetPostsResponse {
  posts: Post[];
  meta: {
    page: number;
    per_page: number;
    has_next: boolean;
    has_prev: boolean;
    status?: string;
    total?: number;  // post_idまたはtagが指定された場合のみ
    total_pages?: number;  // post_idまたはtagが指定された場合のみ
    post_ids_count?: number;  // post_idが指定された場合のみ
    tag?: string;  // tagが指定された場合のみ
  };
}

/**
 * Get Posts APIのパラメータ
 */
export interface GetPostsParams {
  page?: number;
  per_page?: number;
  status?: 'published' | 'draft';
  post_ids?: string[];  // 特定のpost_idでフィルタリング（カンマ区切りで送信、最大100件）
  lang?: 'ja' | 'en';  // 言語指定（ja: 日本語, en: 英語）
  tag?: string;  // タグのslugでフィルタリング（単一）。tags 指定時は無視
  tags?: string[];  // タグのslugでフィルタリング（複数）。指定タグのいずれかを持つ記事（Lambda側で対応必要）
  category?: string;  // カテゴリーのslugでフィルタリング
  skipAuth?: boolean;  // trueの場合、認証トークン（Authorizationヘッダ）を送信しない
}

/**
 * Get Post API の戻り値（記事詳細 + 関連記事）
 */
export type GetPostResponse = Post & { related_posts?: Post[] };

/**
 * API GatewayのベースエンドポイントURLを取得
 * 環境変数 VITE_LAMBDA_ENDPOINT から取得し、環境に応じてステージを追加
 * 
 * 設定例:
 * - 開発環境: https://5iu8q9zvu1.execute-api.us-east-1.amazonaws.com → /dev を追加
 * - 本番環境: https://<api-gateway-id>.execute-api.<region>.amazonaws.com → /prod を追加
 * 
 * 注意: 
 * - 環境変数に既にステージが含まれている場合はそのまま使用
 * - 末尾にスラッシュは不要（コード内で追加されます）
 */
const getApiEndpoint = (): string => {
  // 環境変数からエンドポイントを取得（優先）
  const endpoint = import.meta.env.VITE_LAMBDA_ENDPOINT;
  if (endpoint) {
    // 末尾のスラッシュを削除して統一
    let cleanEndpoint = endpoint.endsWith('/') ? endpoint.slice(0, -1) : endpoint;
    
    // CloudFront URLかどうかをチェック（cloudfront.net を含む場合）
    const isCloudFront = cleanEndpoint.includes('cloudfront.net');
    
    if (isCloudFront) {
      // CloudFront URLの場合はそのまま使用（既に完全なパスが含まれている可能性がある）
      return cleanEndpoint;
    }
    
    // 既にステージが含まれているかチェック（例: /dev, /prod など）
    // amazonaws.com の後のパスセグメントを確認
    const urlMatch = cleanEndpoint.match(/amazonaws\.com(\/.*)?$/);
    const pathAfterAws = urlMatch ? (urlMatch[1] || '') : '';
    const hasStage = pathAfterAws.length > 0 && pathAfterAws !== '/';
    
    // ステージが含まれていない場合、環境に応じて追加
    if (!hasStage) {
      if (import.meta.env.DEV) {
        // 開発環境: /dev を追加
        cleanEndpoint = `${cleanEndpoint}/dev`;
      } else if (import.meta.env.PROD) {
        // 本番環境: /prod を追加（環境変数に含まれていない場合）
        cleanEndpoint = `${cleanEndpoint}/prod`;
      }
    }
    
    return cleanEndpoint;
  }
  
  // 環境変数が設定されていない場合のフォールバック
  // 開発時のみ使用（本番環境では環境変数を必ず設定すること）
  const baseUrl = 'https://5iu8q9zvu1.execute-api.us-east-1.amazonaws.com';
  if (import.meta.env.DEV) {
    return `${baseUrl}/dev`;
  } else if (import.meta.env.PROD) {
    return `${baseUrl}/prod`;
  }
  return `${baseUrl}/dev`; // デフォルトは開発環境
};

/**
 * ブログ投稿一覧を取得する
 * 
 * @param params クエリパラメータ
 * @returns 投稿一覧とメタ情報
 */
export const getPosts = async (params: GetPostsParams = {}): Promise<GetPostsResponse> => {
  const { page = 1, per_page = 20, status, post_ids, lang, tag, tags, category, skipAuth = false } = params;
  
  // クエリパラメータを構築
  const queryParams = new URLSearchParams();
  queryParams.append('page', page.toString());
  queryParams.append('per_page', per_page.toString());
  if (status) {
    queryParams.append('status', status);
  }
  if (post_ids && post_ids.length > 0) {
    // post_idsをカンマ区切りで送信
    queryParams.append('post_ids', post_ids.join(','));
  }
  if (lang) {
    queryParams.append('lang', lang);
  }
  if (tags && tags.length > 0) {
    // 複数タグはカンマ区切りで送信（Lambda側で tags パラメータ対応が必要）
    queryParams.append('tags', tags.map((t) => t.trim()).filter(Boolean).join(','));
  } else if (tag) {
    queryParams.append('tag', tag);
  }
  if (category) {
    queryParams.append('category', category);
  }
  
  const endpoint = getApiEndpoint();
  // API Gateway経由でエンドポイントを構築
  // 例: https://5iu8q9zvu1.execute-api.us-east-1.amazonaws.com/dev/posts?status=published&page=1
  const url = `${endpoint}/posts?${queryParams.toString()}`;
  
  try {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    
    if (!skipAuth) {
      const idToken = await getCurrentIdToken();
      if (idToken) {
        headers['Authorization'] = `Bearer ${idToken}`;
      }
    }
    
    const response = await fetch(url, {
      method: 'GET',
      headers,
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      
      // ステータスコードに応じたエラーメッセージを提供
      if (response.status === 403) {
        // 認証エラーの場合
        let errorMessage = 'APIへのアクセスが拒否されました';
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message === 'Missing Authentication Token') {
            errorMessage = '認証トークンが不足しています。API Gatewayの設定を確認してください。';
          } else if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        const error = new Error(errorMessage);
        (error as any).isAuthError = true;
        throw error;
      } else if (response.status === 500) {
        // サーバーエラーの場合
        let errorMessage = 'サーバーエラーが発生しました';
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = `サーバーエラー: ${errorData.message}`;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      } else if (response.status >= 400 && response.status < 500) {
        // クライアントエラーの場合（400-499）
        let errorMessage = `リクエストエラー (${response.status})`;
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      } else if (response.status >= 500) {
        // サーバーエラーの場合（500-599）
        let errorMessage = `サーバーエラー (${response.status})`;
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = `サーバーエラー: ${errorData.message}`;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      }
      
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    // API Gateway経由の場合、レスポンスは直接JSONオブジェクトとして返される
    const responseData = await response.json();
    
    // API Gateway経由の場合、レスポンスは直接JSONオブジェクト
    // ただし、Lambda関数の実装によってはbodyフィールドに含まれる可能性があるため、
    // 両方の形式に対応
    let data: GetPostsResponse;
    if (responseData.body) {
      // bodyフィールドが存在する場合（Lambda関数の直接レスポンス形式）
      data = typeof responseData.body === 'string' 
        ? JSON.parse(responseData.body) 
        : responseData.body;
    } else {
      // 直接JSONオブジェクトの場合（API Gateway経由の標準形式）
      data = responseData;
    }
    
    return data;
  } catch (error) {
    // CORSエラーの場合、より分かりやすいエラーメッセージを提供
    if (error instanceof TypeError && error.message === 'Failed to fetch') {
      throw new Error('CORSエラー: API GatewayのCORS設定を確認してください。開発環境では、API GatewayでCORSを有効化する必要があります。');
    }
    
    throw error;
  }
};

/**
 * ブログ投稿の詳細を取得する
 * 
 * @param slug 記事のスラッグ
 * @param lang 言語指定（ja: 日本語, en: 英語）
 * @returns 投稿の詳細情報（related_posts が含まれる場合あり）
 */
export const getPost = async (slug: string, lang?: 'ja' | 'en'): Promise<GetPostResponse> => {
  const endpoint = getApiEndpoint();
  // API Gateway経由でエンドポイントを構築
  // 例: https://5iu8q9zvu1.execute-api.us-east-1.amazonaws.com/dev/posts/{slug}
  // slugに特殊文字が含まれる可能性があるため、URLエンコードを適用
  // ただし、スラッシュ（/）はエンコードしない（パス区切りとして使用されるため）
  const encodedSlug = encodeURIComponent(slug);
  const queryParams = new URLSearchParams();
  if (lang) {
    queryParams.append('lang', lang);
  }
  const queryString = queryParams.toString();
  const url = `${endpoint}/posts/${encodedSlug}${queryString ? `?${queryString}` : ''}`;
  
  try {
    const idToken = await getCurrentIdToken();
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    
    if (idToken) {
      headers['Authorization'] = `Bearer ${idToken}`;
    }
    
    const response = await fetch(url, {
      method: 'GET',
      headers,
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      
      // ステータスコードに応じたエラーメッセージを提供
      if (response.status === 404) {
        throw new Error('記事が見つかりません');
      } else if (response.status === 403) {
        let errorMessage = 'APIへのアクセスが拒否されました';
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message === 'Missing Authentication Token') {
            errorMessage = '認証トークンが不足しています。API Gatewayの設定を確認してください。';
          } else if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        const error = new Error(errorMessage);
        (error as any).isAuthError = true;
        throw error;
      } else if (response.status === 500) {
        let errorMessage = 'サーバーエラーが発生しました';
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = `サーバーエラー: ${errorData.message}`;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      } else if (response.status === 400) {
        let errorMessage = 'リクエストが不正です';
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = errorData.message;
            // slugが必要ですというエラーの場合、より詳細なメッセージを提供
            if (errorData.message.includes('slug')) {
              errorMessage = `記事の取得に失敗しました: ${errorData.message}。URLを確認してください。`;
            }
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      } else if (response.status >= 400 && response.status < 500) {
        let errorMessage = `リクエストエラー (${response.status})`;
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      } else if (response.status >= 500) {
        let errorMessage = `サーバーエラー (${response.status})`;
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = `サーバーエラー: ${errorData.message}`;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      }
      
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    // API Gateway経由の場合、レスポンスは直接JSONオブジェクトとして返される
    const responseData = await response.json();

    // 両方の形式に対応（related_posts は API が返す場合に含まれる）
    let data: GetPostResponse;
    if (responseData.body) {
      data = typeof responseData.body === 'string'
        ? JSON.parse(responseData.body)
        : responseData.body;
    } else {
      data = responseData;
    }

    return data;
  } catch (error) {
    // CORSエラーの場合、より分かりやすいエラーメッセージを提供
    if (error instanceof TypeError && error.message === 'Failed to fetch') {
      throw new Error('CORSエラー: API GatewayのCORS設定を確認してください。開発環境では、API GatewayでCORSを有効化する必要があります。');
    }

    throw error;
  }
};

/**
 * ブログ投稿のステータスを更新する（管理画面用）
 * 
 * @param postId 更新する記事のpost_id
 * @param status 新しいステータス（'draft' | 'published'）
 * @returns 更新成功時はvoid
 */
export const updatePostStatus = async (postId: string, status: 'draft' | 'published'): Promise<void> => {
  const endpoint = getApiEndpoint();
  // 管理画面用のエンドポイント
  // 例: https://5iu8q9zvu1.execute-api.us-east-1.amazonaws.com/dev/admin/posts
  const url = `${endpoint}/admin/posts`;
  
  try {
    const idToken = await getCurrentIdToken();
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    
    if (idToken) {
      headers['Authorization'] = `Bearer ${idToken}`;
    }
    
    const response = await fetch(url, {
      method: 'PATCH',
      headers,
      body: JSON.stringify({
        post_id: postId,
        status: status,
      }),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      
      // ステータスコードに応じたエラーメッセージを提供
      if (response.status === 404) {
        throw new Error('記事が見つかりません');
      } else if (response.status === 403) {
        let errorMessage = 'APIへのアクセスが拒否されました';
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message === 'Missing Authentication Token') {
            errorMessage = '認証トークンが不足しています。API Gatewayの設定を確認してください。';
          } else if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        const error = new Error(errorMessage);
        (error as any).isAuthError = true;
        throw error;
      } else if (response.status === 500) {
        let errorMessage = 'サーバーエラーが発生しました';
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = `サーバーエラー: ${errorData.message}`;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      } else if (response.status >= 400 && response.status < 500) {
        // クライアントエラーの場合（400-499）
        let errorMessage = `リクエストエラー (${response.status})`;
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      } else if (response.status >= 500) {
        // サーバーエラーの場合（500-599）
        let errorMessage = `サーバーエラー (${response.status})`;
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = `サーバーエラー: ${errorData.message}`;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      }
      
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    // 更新成功時はレスポンスボディがない可能性があるため、何も返さない
    return;
  } catch (error) {
    // CORSエラーの場合、より分かりやすいエラーメッセージを提供
    if (error instanceof TypeError && error.message === 'Failed to fetch') {
      throw new Error('CORSエラー: API GatewayのCORS設定を確認してください。開発環境では、API GatewayでCORSを有効化する必要があります。');
    }
    
    throw error;
  }
};

/**
 * ブログ投稿を作成する（管理画面用）
 * 
 * @param postData 作成する記事のデータ
 * @returns 作成された記事の情報
 */
export interface CreatePostData {
  title: string;
  slug: string;
  content: string;
  status: 'draft' | 'published';
  excerpt?: string;
  category?: string;
  tags?: string[];
  thumbnail_url?: string | null;
  titleEn?: string;  // 英語タイトル
  contentEn?: string;  // 英語本文
  excerptEn?: string;  // 英語概要
}

export const createPost = async (postData: CreatePostData): Promise<Post> => {
  const endpoint = getApiEndpoint();
  // 管理画面用のエンドポイント
  // 例: https://5iu8q9zvu1.execute-api.us-east-1.amazonaws.com/dev/admin/posts
  const url = `${endpoint}/admin/posts`;
  
  try {
    const idToken = await getCurrentIdToken();
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    
    if (idToken) {
      headers['Authorization'] = `Bearer ${idToken}`;
    }
    
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(postData),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      
      // ステータスコードに応じたエラーメッセージを提供
      if (response.status === 400) {
        let errorMessage = 'リクエストが不正です';
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      } else if (response.status === 403) {
        let errorMessage = 'APIへのアクセスが拒否されました';
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message === 'Missing Authentication Token') {
            errorMessage = '認証トークンが不足しています。API Gatewayの設定を確認してください。';
          } else if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        const error = new Error(errorMessage);
        (error as any).isAuthError = true;
        throw error;
      } else if (response.status === 500) {
        let errorMessage = 'サーバーエラーが発生しました';
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = `サーバーエラー: ${errorData.message}`;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      } else if (response.status >= 400 && response.status < 500) {
        // クライアントエラーの場合（400-499）
        let errorMessage = `リクエストエラー (${response.status})`;
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      } else if (response.status >= 500) {
        // サーバーエラーの場合（500-599）
        let errorMessage = `サーバーエラー (${response.status})`;
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = `サーバーエラー: ${errorData.message}`;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      }
      
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    // API Gateway経由の場合、レスポンスは直接JSONオブジェクトとして返される
    const responseData = await response.json();
    
    // API Gateway経由の場合、レスポンスは直接JSONオブジェクト
    // ただし、Lambda関数の実装によってはbodyフィールドに含まれる可能性があるため、
    // 両方の形式に対応
    let data: Post;
    if (responseData.body) {
      // bodyフィールドが存在する場合（Lambda関数の直接レスポンス形式）
      data = typeof responseData.body === 'string' 
        ? JSON.parse(responseData.body) 
        : responseData.body;
    } else {
      // 直接JSONオブジェクトの場合（API Gateway経由の標準形式）
      data = responseData;
    }
    
    return data;
  } catch (error) {
    // CORSエラーの場合、より分かりやすいエラーメッセージを提供
    if (error instanceof TypeError && error.message === 'Failed to fetch') {
      throw new Error('CORSエラー: API GatewayのCORS設定を確認してください。開発環境では、API GatewayでCORSを有効化する必要があります。');
    }
    
    throw error;
  }
};

/**
 * ブログ投稿を更新する（管理画面用）
 * 
 * @param postId 更新する記事のpost_id
 * @param postData 更新する記事のデータ
 * @returns 更新された記事の情報
 */
export interface UpdatePostData {
  title: string;
  slug: string;
  content: string;
  status: 'draft' | 'published';
  excerpt?: string;
  category?: string;
  tags?: string[];
  thumbnail_url?: string | null;
  titleEn?: string;  // 英語タイトル
  contentEn?: string;  // 英語本文
  excerptEn?: string;  // 英語概要
}

export const updatePost = async (postId: string, postData: UpdatePostData): Promise<Post> => {
  const endpoint = getApiEndpoint();
  // 管理画面用のエンドポイント
  // 例: https://5iu8q9zvu1.execute-api.us-east-1.amazonaws.com/dev/admin/posts
  const url = `${endpoint}/admin/posts`;
  
  try {
    const idToken = await getCurrentIdToken();
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    
    if (idToken) {
      headers['Authorization'] = `Bearer ${idToken}`;
    }
    
    const response = await fetch(url, {
      method: 'PATCH',
      headers,
      body: JSON.stringify({
        post_id: postId,
        ...postData,
      }),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      
      // ステータスコードに応じたエラーメッセージを提供
      if (response.status === 400) {
        let errorMessage = 'リクエストが不正です';
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      } else if (response.status === 404) {
        throw new Error('記事が見つかりません');
      } else if (response.status === 403) {
        let errorMessage = 'APIへのアクセスが拒否されました';
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message === 'Missing Authentication Token') {
            errorMessage = '認証トークンが不足しています。API Gatewayの設定を確認してください。';
          } else if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        const error = new Error(errorMessage);
        (error as any).isAuthError = true;
        throw error;
      } else if (response.status === 500) {
        let errorMessage = 'サーバーエラーが発生しました';
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = `サーバーエラー: ${errorData.message}`;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      } else if (response.status >= 400 && response.status < 500) {
        // クライアントエラーの場合（400-499）
        let errorMessage = `リクエストエラー (${response.status})`;
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      } else if (response.status >= 500) {
        // サーバーエラーの場合（500-599）
        let errorMessage = `サーバーエラー (${response.status})`;
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = `サーバーエラー: ${errorData.message}`;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      }
      
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    // API Gateway経由の場合、レスポンスは直接JSONオブジェクトとして返される
    const responseData = await response.json();
    
    // API Gateway経由の場合、レスポンスは直接JSONオブジェクト
    // ただし、Lambda関数の実装によってはbodyフィールドに含まれる可能性があるため、
    // 両方の形式に対応
    let data: Post;
    if (responseData.body) {
      // bodyフィールドが存在する場合（Lambda関数の直接レスポンス形式）
      data = typeof responseData.body === 'string' 
        ? JSON.parse(responseData.body) 
        : responseData.body;
    } else {
      // 直接JSONオブジェクトの場合（API Gateway経由の標準形式）
      data = responseData;
    }
    
    return data;
  } catch (error) {
    // CORSエラーの場合、より分かりやすいエラーメッセージを提供
    if (error instanceof TypeError && error.message === 'Failed to fetch') {
      throw new Error('CORSエラー: API GatewayのCORS設定を確認してください。開発環境では、API GatewayでCORSを有効化する必要があります。');
    }
    
    throw error;
  }
};

/**
 * ブログ投稿を削除する（管理画面用）
 * 
 * @param postId 削除する記事のpost_id
 * @returns 削除成功時はvoid
 */
export const deletePost = async (postId: string): Promise<void> => {
  const endpoint = getApiEndpoint();
  // 管理画面用のエンドポイント
  // 例: https://5iu8q9zvu1.execute-api.us-east-1.amazonaws.com/dev/admin/posts
  const url = `${endpoint}/admin/posts`;
  
  try {
    const idToken = await getCurrentIdToken();
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    
    if (idToken) {
      headers['Authorization'] = `Bearer ${idToken}`;
    }
    
    const response = await fetch(url, {
      method: 'DELETE',
      headers,
      body: JSON.stringify({
        post_id: postId,
      }),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      
      // ステータスコードに応じたエラーメッセージを提供
      if (response.status === 404) {
        throw new Error('記事が見つかりません');
      } else if (response.status === 403) {
        let errorMessage = 'APIへのアクセスが拒否されました';
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message === 'Missing Authentication Token') {
            errorMessage = '認証トークンが不足しています。API Gatewayの設定を確認してください。';
          } else if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        const error = new Error(errorMessage);
        (error as any).isAuthError = true;
        throw error;
      } else if (response.status === 500) {
        let errorMessage = 'サーバーエラーが発生しました';
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = `サーバーエラー: ${errorData.message}`;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      } else if (response.status >= 400 && response.status < 500) {
        // クライアントエラーの場合（400-499）
        let errorMessage = `リクエストエラー (${response.status})`;
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      } else if (response.status >= 500) {
        // サーバーエラーの場合（500-599）
        let errorMessage = `サーバーエラー (${response.status})`;
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = `サーバーエラー: ${errorData.message}`;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      }
      
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    // 削除成功時はレスポンスボディがない可能性があるため、何も返さない
    return;
  } catch (error) {
    // CORSエラーの場合、より分かりやすいエラーメッセージを提供
    if (error instanceof TypeError && error.message === 'Failed to fetch') {
      throw new Error('CORSエラー: API GatewayのCORS設定を確認してください。開発環境では、API GatewayでCORSを有効化する必要があります。');
    }
    
    throw error;
  }
};

/**
 * コンテンツ生成リクエスト
 */
export interface GeneratePostContentRequest {
  content: string;
  prompt: string;
}

/**
 * コンテンツ生成レスポンス
 */
export interface GeneratePostContentResponse {
  title: string;
  excerpt: string;
  slug: string;
  original_slug?: string;
  titleEn: string; // 英語タイトル（常に生成される）
  excerptEn: string; // 英語概要（常に生成される）
}

/**
 * ブログ記事のコンテンツ生成用JSONスキーマ
 */
const generatePostContentJsonSchema = {
  type: 'object',
  properties: {
    title: {
      type: 'string',
      description: 'ブログ記事のタイトル（日本語）。本文の見出しの言葉遣いやトーンをできるだけ活かし、「〜する方法」「〜を解決する」のような紋切り型の言い回しに置き換えないこと。',
    },
    excerpt: {
      type: 'string',
      description: 'ブログ記事の概要（100文字程度、日本語）。本文の語り口を踏襲した、自然で読みやすい文章にすること。「〜を紹介します」「〜を解説します」のようなレポート調の言い回しは避けること。',
    },
    slug: {
      type: 'string',
      description: 'URLフレンドリーなSlug（小文字の英数字とハイフンのみ、30文字程度）',
    },
    titleEn: {
      type: 'string',
      description: 'ブログ記事のタイトル（英語、自然な翻訳）',
    },
    excerptEn: {
      type: 'string',
      description: 'ブログ記事の概要（英語、100文字程度、自然な翻訳）。excerptと同様に、レポート調を避けた自然な文章にすること。',
    },
  },
  required: ['title', 'excerpt', 'slug', 'titleEn', 'excerptEn'],
};

/**
 * ブログ記事の本文からタイトル、概要、Slugを同時に自動生成する
 * AWS Bedrock（Claude Haiku 4.5、inference profile経由）を使用
 * 構造化出力（structured output）を使用して確実に値を取得
 * 
 * @param request 生成リクエスト（本文）
 * @returns 生成されたタイトル、概要、Slug
 */
export const generatePostContent = async (
  request: GeneratePostContentRequest
): Promise<GeneratePostContentResponse> => {
  const { content, prompt } = request;
  
  // バリデーション
  if (!content || typeof content !== 'string' || content.trim() === '') {
    throw new Error('contentパラメータが必須です');
  }
  if (!prompt || typeof prompt !== 'string' || prompt.trim() === '') {
    throw new Error('promptパラメータが必須です');
  }
  
  const endpoint = getApiEndpoint();
  // 管理画面用のエンドポイント
  const url = `${endpoint}/admin/generate-post-content`;
  
  try {
    const idToken = await getCurrentIdToken();
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    
    if (idToken) {
      headers['Authorization'] = `Bearer ${idToken}`;
    }
    
    const requestBody: any = {
      prompt: prompt.trim(),
      jsonSchema: generatePostContentJsonSchema,
      toolName: 'blog_content_generator',
      toolDescription: 'ブログ記事の本文からタイトル、概要、Slug、英語タイトル、英語概要を生成します。',
    };
    
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(requestBody),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      
      // ステータスコードに応じたエラーメッセージを提供
      if (response.status === 400) {
        let errorMessage = 'リクエストエラー';
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      } else if (response.status === 401) {
        // 401 Unauthorizedエラーの場合
        let errorMessage = '認証が必要です';
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        const error = new Error(errorMessage);
        (error as any).isAuthError = true;
        throw error;
      } else if (response.status === 403) {
        // 認証エラーの場合
        let errorMessage = 'APIへのアクセスが拒否されました';
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message === 'Missing Authentication Token') {
            errorMessage = '認証トークンが不足しています。API Gatewayの設定を確認してください。';
          } else if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        const error = new Error(errorMessage);
        (error as any).isAuthError = true;
        throw error;
      } else if (response.status === 500) {
        let errorMessage = 'サーバーエラーが発生しました';
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      } else if (response.status === 504) {
        // 504 Gateway Timeoutエラーの場合
        let errorMessage = 'リクエストがタイムアウトしました。AI生成に時間がかかっている可能性があります。しばらく待ってから再度お試しください。';
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      }
      
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    // レスポンスデータにtitle, excerpt, slug, titleEn, excerptEnが含まれていることを確認
    if (!data.title || !data.excerpt || !data.slug) {
      throw new Error('生成されたコンテンツが不足しています');
    }
    if (!data.titleEn || !data.excerptEn) {
      throw new Error('生成された英語翻訳コンテンツが不足しています');
    }
    return data as GeneratePostContentResponse;
  } catch (error) {
    // CORSエラーの場合、より分かりやすいエラーメッセージを提供
    if (error instanceof TypeError && error.message === 'Failed to fetch') {
      throw new Error(
        'CORSエラー: /admin/generate-post-contentエンドポイントのCORS設定を確認してください。' +
        'API GatewayでOPTIONSメソッドが有効化され、適切なCORSヘッダーが設定されている必要があります。' +
        '開発環境（localhost:5173）からのリクエストが許可されているか確認してください。'
      );
    }
    
    throw error;
  }
};

/**
 * タグ生成リクエスト
 */
export interface GenerateTagsRequest {
  content: string;
  prompt: string;
  existingTags?: Array<{
    id: string;
    name: string;
    nameEn: string;
    slug: string;
  }>;
}

/**
 * タグ生成レスポンス
 */
export interface GenerateTagsResponse {
  tags: Array<{
    id: string | null;
    name: string;
    nameEn: string;
    slug: string;
    isNew: boolean;
  }>;
}

/**
 * ブログ記事のタグ生成用JSONスキーマ
 */
const generateTagsJsonSchema = {
  type: 'object',
  properties: {
    tags: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          id: {
            type: ['string', 'null'],
            description: '既存タグのID（既存タグの場合はID、新規タグの場合はnull）',
          },
          name: {
            type: 'string',
            description: 'タグ名（日本語）',
          },
          nameEn: {
            type: 'string',
            description: 'タグ名（英語）',
          },
          slug: {
            type: 'string',
            description: 'URLフレンドリーなSlug（小文字の英数字とハイフンのみ）',
          },
          isNew: {
            type: 'boolean',
            description: '新規タグかどうか（既存タグの場合はfalse、新規タグの場合はtrue）',
          },
        },
        required: ['name', 'nameEn', 'slug', 'isNew'],
      },
    },
  },
  required: ['tags'],
};

/**
 * ブログ記事の本文からタグを自動生成する
 * AWS Bedrock（Claude Haiku 4.5、inference profile経由）を使用
 * 構造化出力（structured output）を使用して確実に値を取得
 * 
 * @param request 生成リクエスト（本文）
 * @returns 生成されたタグ
 */
export const generateTags = async (
  request: GenerateTagsRequest
): Promise<GenerateTagsResponse> => {
  const { content, prompt } = request;
  
  // バリデーション
  if (!content || typeof content !== 'string' || content.trim() === '') {
    throw new Error('contentパラメータが必須です');
  }
  if (!prompt || typeof prompt !== 'string' || prompt.trim() === '') {
    throw new Error('promptパラメータが必須です');
  }
  
  const endpoint = getApiEndpoint();
  // 管理画面用のエンドポイント（generatePostContentと同じエンドポイントを使用）
  const url = `${endpoint}/admin/generate-post-content`;
  
  try {
    const idToken = await getCurrentIdToken();
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    
    if (idToken) {
      headers['Authorization'] = `Bearer ${idToken}`;
    }
    
    const requestBody: any = {
      prompt: prompt.trim(),
      jsonSchema: generateTagsJsonSchema,
      toolName: 'blog_tags_generator',
      toolDescription: 'ブログ記事の本文から適切なタグを生成します。既存タグがある場合は既存タグから選択し、ない場合は新規タグを作成します。',
    };
    
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(requestBody),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      
      // ステータスコードに応じたエラーメッセージを提供
      if (response.status === 400) {
        let errorMessage = 'リクエストエラー';
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      } else if (response.status === 401) {
        // 401 Unauthorizedエラーの場合
        let errorMessage = '認証が必要です';
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        const error = new Error(errorMessage);
        (error as any).isAuthError = true;
        throw error;
      } else if (response.status === 403) {
        // 認証エラーの場合
        let errorMessage = 'APIへのアクセスが拒否されました';
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message === 'Missing Authentication Token') {
            errorMessage = '認証トークンが不足しています。API Gatewayの設定を確認してください。';
          } else if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        const error = new Error(errorMessage);
        (error as any).isAuthError = true;
        throw error;
      } else if (response.status === 500) {
        let errorMessage = 'サーバーエラーが発生しました';
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      } else if (response.status === 504) {
        // 504 Gateway Timeoutエラーの場合
        let errorMessage = 'リクエストがタイムアウトしました。AI生成に時間がかかっている可能性があります。しばらく待ってから再度お試しください。';
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      }
      
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    // レスポンスデータにtagsが含まれていることを確認
    if (!data.tags || !Array.isArray(data.tags)) {
      throw new Error('生成されたタグが不足しています');
    }
    return data as GenerateTagsResponse;
  } catch (error) {
    // CORSエラーの場合、より分かりやすいエラーメッセージを提供
    if (error instanceof TypeError && error.message === 'Failed to fetch') {
      throw new Error(
        'CORSエラー: /admin/generate-post-contentエンドポイントのCORS設定を確認してください。' +
        'API GatewayでOPTIONSメソッドが有効化され、適切なCORSヘッダーが設定されている必要があります。' +
        '開発環境（localhost:5173）からのリクエストが許可されているか確認してください。'
      );
    }
    
    throw error;
  }
};

/**
 * すべての公開記事のHTMLファイルを一括再生成する
 * 
 * @returns 再生成結果
 */
export const regenerateAllPostHtml = async (): Promise<{
  message: string;
  total: number;
  success: number;
  error: number;
  errors: Array<{ slug: string; lang?: string; error: string }>;
}> => {
  const endpoint = getApiEndpoint();
  // 管理画面用のエンドポイント
  // 例: https://5iu8q9zvu1.execute-api.us-east-1.amazonaws.com/dev/admin/generate-all-post-html
  const url = `${endpoint}/admin/generate-all-post-html`;
  
  try {
    const idToken = await getCurrentIdToken();
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`,
      },
      body: JSON.stringify({}),
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
      const errorMessage = errorData.message || `HTTP error! status: ${response.status}`;
      throw new Error(errorMessage);
    }
    
    const responseData = await response.json();
    
    // API Gateway経由の場合、レスポンスは直接JSONオブジェクト
    // ただし、Lambda関数の実装によってはbodyフィールドに含まれる可能性があるため、
    // 両方の形式に対応
    let data;
    if (responseData.body) {
      data = typeof responseData.body === 'string' 
        ? JSON.parse(responseData.body) 
        : responseData.body;
    } else {
      data = responseData;
    }
    
    return data;
  } catch (error) {
    if (error instanceof TypeError && error.message === 'Failed to fetch') {
      throw new Error(
        'CORSエラー: /admin/generate-all-post-htmlエンドポイントのCORS設定を確認してください。' +
        'API GatewayでOPTIONSメソッドが有効化され、適切なCORSヘッダーが設定されている必要があります。' +
        '開発環境（localhost:5173）からのリクエストが許可されているか確認してください。'
      );
    }
    
    throw error;
  }
};

/**
 * キャッシュウォーミングを実行する
 * 
 * @returns ウォーミング結果
 */
export const warmCache = async (): Promise<{
  message: string;
  total: number;
  success: number;
  error: number;
  errors: Array<{ url: string; error: string }>;
}> => {
  const endpoint = getApiEndpoint();
  // 管理画面用のエンドポイント
  const url = `${endpoint}/admin/warm-cache`;
  
  try {
    const idToken = await getCurrentIdToken();
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`,
      },
      body: JSON.stringify({}),
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
      const errorMessage = errorData.message || `HTTP error! status: ${response.status}`;
      throw new Error(errorMessage);
    }
    
    const responseData = await response.json();
    
    // API Gateway経由の場合、レスポンスは直接JSONオブジェクト
    // ただし、Lambda関数の実装によってはbodyフィールドに含まれる可能性があるため、
    // 両方の形式に対応
    let data;
    if (responseData.body) {
      data = typeof responseData.body === 'string' 
        ? JSON.parse(responseData.body) 
        : responseData.body;
    } else {
      data = responseData;
    }
    
    return data;
  } catch (error) {
    if (error instanceof TypeError && error.message === 'Failed to fetch') {
      throw new Error(
        'CORSエラー: /admin/warm-cacheエンドポイントのCORS設定を確認してください。' +
        'API GatewayでOPTIONSメソッドが有効化され、適切なCORSヘッダーが設定されている必要があります。' +
        '開発環境（localhost:5173）からのリクエストが許可されているか確認してください。'
      );
    }
    
    throw error;
  }
};